import { ArrowUpCircle, ArrowDownCircle } from 'lucide-react';
import { useDateFormatter } from '@/shared/hooks';
import type { StatementMovement } from '@yawi/types';

interface AccountStatementMovementsProps {
  movements: StatementMovement[];
}

const formatCurrency = (amount: number) => {
  return new Intl.NumberFormat('es-MX', {
    style: 'currency',
    currency: 'MXN',
  }).format(Math.abs(amount));
};

const getStatusBadge = (movement: StatementMovement) => {
  if (movement.type === 'CHARGE') {
    const status = movement.chargeStatus;
    const styles: Record<string, string> = {
      PENDING: 'bg-yellow-100 text-yellow-800',
      PARTIAL: 'bg-orange-100 text-orange-800',
      PAID: 'bg-green-100 text-green-800',
      CANCELLED: 'bg-slate-100 text-slate-500',
    };
    const labels: Record<string, string> = {
      PENDING: 'Pendiente',
      PARTIAL: 'Parcial',
      PAID: 'Pagado',
      CANCELLED: 'Cancelado',
    };
    return status ? (
      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${styles[status] || ''}`}>
        {labels[status] || status}
      </span>
    ) : null;
  }

  if (movement.type === 'PAYMENT') {
    const status = movement.paymentStatus;
    const styles: Record<string, string> = {
      PENDING: 'bg-yellow-100 text-yellow-800',
      VERIFYING: 'bg-blue-100 text-blue-800',
      VERIFIED: 'bg-green-100 text-green-800',
      REJECTED: 'bg-red-100 text-red-800',
    };
    const labels: Record<string, string> = {
      PENDING: 'Pendiente',
      VERIFYING: 'Verificando',
      VERIFIED: 'Verificado',
      REJECTED: 'Rechazado',
    };
    return status ? (
      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${styles[status] || ''}`}>
        {labels[status] || status}
      </span>
    ) : null;
  }

  return null;
};

export const AccountStatementMovements = ({ movements }: AccountStatementMovementsProps) => {
  const { formatDate } = useDateFormatter();

  if (movements.length === 0) {
    return (
      <div className="text-center py-12 text-slate-500">
        No hay movimientos en el período seleccionado
      </div>
    );
  }

  return (
    <div className="overflow-x-auto">
      <table className="w-full">
        <thead>
          <tr className="border-b border-slate-200">
            <th className="text-left py-3 px-4 text-sm font-medium text-slate-600">Fecha</th>
            <th className="text-left py-3 px-4 text-sm font-medium text-slate-600">Tipo</th>
            <th className="text-left py-3 px-4 text-sm font-medium text-slate-600">Concepto</th>
            <th className="text-right py-3 px-4 text-sm font-medium text-slate-600">Monto</th>
            <th className="text-right py-3 px-4 text-sm font-medium text-slate-600">Saldo</th>
            <th className="text-center py-3 px-4 text-sm font-medium text-slate-600">Estado</th>
          </tr>
        </thead>
        <tbody>
          {movements.map((movement) => (
            <tr key={movement.id} className="border-b border-slate-100 hover:bg-slate-50">
              <td className="py-3 px-4 text-sm text-slate-600">
                {formatDate(movement.date)}
              </td>
              <td className="py-3 px-4">
                <div className="flex items-center gap-2">
                  {movement.type === 'CHARGE' ? (
                    <ArrowUpCircle size={18} className="text-red-500" />
                  ) : (
                    <ArrowDownCircle size={18} className="text-green-500" />
                  )}
                  <span className="text-sm font-medium text-slate-700">
                    {movement.type === 'CHARGE' ? 'Cargo' : 'Pago'}
                  </span>
                </div>
              </td>
              <td className="py-3 px-4">
                <div className="text-sm text-slate-800">{movement.concept}</div>
                {movement.period && (
                  <div className="text-xs text-slate-400">Periodo: {movement.period}</div>
                )}
                {movement.reference && (
                  <div className="text-xs text-slate-400">Ref: {movement.reference}</div>
                )}
              </td>
              <td className={`py-3 px-4 text-sm font-medium text-right ${
                movement.type === 'CHARGE' ? 'text-red-600' : 'text-green-600'
              }`}>
                {movement.type === 'CHARGE' ? '+' : '-'}{formatCurrency(movement.amount)}
              </td>
              <td className="py-3 px-4 text-sm font-medium text-right text-slate-700">
                {formatCurrency(movement.runningBalance)}
              </td>
              <td className="py-3 px-4 text-center">
                {getStatusBadge(movement)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};
